import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import CustomTableV2 from '../CustomTableV2'
import UnpaidOrdersTableRow from './row'
import KeyBindings from '../../../keybindings'
import { OrderTableWrapper } from '../../OrdersTable/index.styled'
import { getUnpaidOrdersByRangeThunk, getUnpaidOrdersThunk } from '../../../../../redux/thunk/order'
import { useTranslation } from 'react-i18next'
import { setUnpaidOrders, setUnpaidOrdersTotalLength } from '../../../../../redux/action/order'
import { setSocketReconnected } from '../../../../../redux/action/socket'
import { logger } from '../../../../../logger/logger'
import { setAppEventZIndex } from '../../../../../redux/action/system'
import { reInitializeOrderFilter, setOrderFilter } from '../../../../../redux/action/orderfilter'
import { selectIsNeededZIndex } from '../../../../../redux/selector/system'

const EVENT_Z_INDEX = 0
const columns = ['0.25fr', '0.3fr', '0.65fr', '1fr', '0.5fr', '0.65fr', '0.5fr']

const UnpaidOrdersTableV2 = React.memo(({ id, reverse, defaultFilter = {}, dateRange, onClick, onDbClick, onEnter, onEscape, onDelete, onNrClick = () => {} }) => {
	const { t } = useTranslation()
	const [activeRow, setActiveRow] = useState(0)
	const isNeededZIndex = useSelector(selectIsNeededZIndex(EVENT_Z_INDEX))
	const orders = useSelector(store => store.order.unpaidOrders)
	const ordersTotalLength = useSelector(store => store.order.unpaidOrdersTotalLength)
	const paymentMethod = useSelector(store => store.orderfilter.paymentMethod)
	const status = useSelector(store => store.orderfilter.status)
	const excludedStatus = useSelector(store => store.orderfilter.excludedStatus)
	const orderType = useSelector(store => store.orderfilter.orderType)
	const processedByUsername = useSelector(store => store.orderfilter.processedByUsername)
	const date = useSelector(store => store.orderfilter.date)
	const reverseSort = useSelector(store => store.orderfilter.reverse)
	const closed = useSelector(store => store.orderfilter.closed)
	const isPaid = useSelector(store => store.orderfilter.isPaid)
	const socketReconnected = useSelector(store => store.socket.socketReconnected)
	const [filtersInitialized, setFiltersInitialized] = useState(false)
	const dispatch = useDispatch()

	const order = orders && orders[activeRow]

	const tableHeadersStatic = useMemo(() => [
		{ title: t('order.table.nr'), onClick: onNrClick },
		{ title: t('order.table.time') },
		{ title: t('order.table.platform') },
		{ title: t('order.table.customer') },
		{ title: t('order.table.sum') },
		{ title: t('order.table.type') },
		{ title: t('order.table.status') },
	], [t, onNrClick])

	useEffect(() => {
		const filter = { ...defaultFilter, reverse, isPaid: false, processedByUsernameWithEmpty: null }
		dispatch(setAppEventZIndex(EVENT_Z_INDEX))
		dispatch(setOrderFilter(filter))
		setFiltersInitialized(true)
		return () => dispatch(reInitializeOrderFilter())
	}, [dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		batch(() => {
			dispatch(setUnpaidOrders([]))
			dispatch(setUnpaidOrdersTotalLength(0))
			setActiveRow(0)
		})
	}, [date, paymentMethod, status, orderType, processedByUsername, isPaid, closed, dateRange?.from, dateRange?.to]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (socketReconnected) {
			logger.info('Load orders by socket reconnect')
			batch(() => {
				dispatch(setUnpaidOrders([]))
				dispatch(setUnpaidOrdersTotalLength(0))
			})
		}
		return () => dispatch(setSocketReconnected(false))
	}, [socketReconnected, dispatch])

	const onLoad = useCallback((page, size) => {
		if (dateRange) {
			dispatch(getUnpaidOrdersByRangeThunk(dateRange.from, dateRange.to))
			return
		}
		dispatch(getUnpaidOrdersThunk(
			date,
			date,
			paymentMethod,
			status,
			excludedStatus,
			orderType,
			processedByUsername,
			page, size, reverseSort, closed, isPaid))
	}, [date, paymentMethod, status, orderType, processedByUsername, reverseSort, closed, isPaid, dateRange?.from, dateRange?.to]) // eslint-disable-line react-hooks/exhaustive-deps

	const handleArrowUp = useCallback(() => {
		if (activeRow < 0) {
			setActiveRow(orders.length)
		} else {
			setActiveRow(activeRow - 1)
		}
	}, [activeRow, orders?.length])

	const handleArrowDown = useCallback(() => {
		if (activeRow < orders?.length) {
			setActiveRow(activeRow + 1)
		} else {
			setActiveRow(0)
		}
	}, [activeRow, orders?.length])

	const handleClick = useCallback((item, i) => {
		setActiveRow(i)
		if (onClick) onClick(item)
	}, [onClick])

	const handleDbClick = useCallback((item, i) => {
		setActiveRow(i)
		if (onDbClick) onDbClick(item)
	}, [onDbClick])

	const handleEnter = useCallback(() => {
		if (!order) return
		if (onEnter) onEnter(order)
	}, [order, onEnter])

	const handleEscape = useCallback(() => {
		if (onEscape) onEscape()
	}, [onEscape])

	const handleDelete = useCallback(() => {
		if (!order) return
		if (onDelete) onDelete(order)
	}, [order, onDelete])

	return (
		<OrderTableWrapper id={id}>
			{filtersInitialized &&
				<CustomTableV2 headers={tableHeadersStatic}
											 onLoad={onLoad}
											 currentLength={orders?.length}
											 totalLength={ordersTotalLength}
											 params={[date, paymentMethod, status, orderType, processedByUsername, reverse, closed]}
											 perLoad={10}
											 columns={columns}
											 isLoaded={true}>
					{orders?.map((item, i) =>
						<UnpaidOrdersTableRow key={i}
																	headers={tableHeadersStatic}
																	columns={columns}
																	item={item}
																	onDoubleClick={() => handleDbClick(item, i)}
																	onClick={() => handleClick(item, i)}
																	isActive={i === activeRow} />)}
				</CustomTableV2>}

			{isNeededZIndex &&
				<KeyBindings arrowUp={handleArrowUp}
										 arrowDown={handleArrowDown}
										 enter={handleEnter}
										 escape={handleEscape}
										 delet={handleDelete} />}
		</OrderTableWrapper>
	)
})

UnpaidOrdersTableV2.displayName = 'UnpaidOrdersTableV2'
export default UnpaidOrdersTableV2
